<!-- 📁 src/components/CompetitionActions.vue -->
<template>
  <div v-if="hasVisibleActions" class="competition-actions mb-2">
    <template v-if="competition.publish_at">
      <!-- Grupo de botones de vista -->
      <div class="view-group">
        <button
            class="action-btn view-athletes"
            @click="$emit('viewAthletes', competition.id)"
        >
          <i class="fa-solid fa-users"></i> {{ $t('competitions.btn_view_athletes') }}
        </button>
        <button
            class="action-btn view-judges"
            @click="$emit('viewJudges', competition.id)"
        >
          <i class="fa-solid fa-gavel"></i> {{ $t('competitions.btn_view_judges') }}
        </button>
        <button
            v-if="competition.started_at"
            class="action-btn view-rounds"
            @click="$emit('viewRounds', competition.id)"
        >
          <i class="fa-solid fa-medal"></i> {{ $t('competitions.btn_view_rounds') }}
        </button>
      </div>

      <!-- Botón de inicio de competición (solo organizador) -->
      <div v-if="isOwnerOrganizer(this.user, competition) && !competitionStarted" class="start-wrapper">
        <button
            class="action-btn start"
            :disabled="!allRequirementsMet"
            @click="$emit('startScoring', competition.id)"
        >
          <i class="fa-solid fa-flag-checkered"></i>
          {{ $t('competitions.btn_start_competition') }}
        </button>
        <div class="requirements-box">
          <p class="req-title text-muted">{{ lblRequirementsTitle }}</p>
          <ul class="req-list">
            <li :class="req3Class">
              {{ lblReqCountdown }}
              <span class="badge" :class="req3BadgeClass">{{ countdownLabel }}</span>
            </li>
            <li :class="req1Class">
              {{ lblReqMinAthletes }} ({{ minAthletes }})
              <span class="badge" :class="req1BadgeClass">{{ approvedApprovedSummary }}/{{ minAthletes }}</span>
            </li>
            <li :class="reqJudgesClass">
              {{ lblReqJudges }} ({{ requiredJudges }})
              <span class="badge" :class="reqJudgesBadgeClass">{{ approvedJudgesCount }}/{{ requiredJudges }}</span>
            </li>
            <li v-if="isLiveCompetition" :class="reqTechClass">
              {{ lblReqTech }}
              <span class="badge" :class="reqTechBadgeClass">{{ techStatusLabel }}</span>
            </li>
            <li v-if="competitionHasFee" :class="req2Class">
              {{ lblReqFeesPaid }}
              <span class="badge" :class="req2BadgeClass">{{ paidAthletesCount }}/{{ approvedAthletesCount }}</span>
            </li>
              <li v-if="totalStagesCount > 0" :class="reqStagesLockClass">
                  {{ lblReqStagesLock }}
                  <span class="badge" :class="reqStagesLockBadgeClass">{{ stagesLockLabel }}</span>
                  <button
                      class="badge badge-action"
                      :class="{ 'badge-action-success': lockAllDone }"
                      :title="$t('competitions.btn_lock_all_stages') || 'Confirmar etapas'"
                      :disabled="lockAllDisabled"
                      @click="openLockAllConfirm"
                  >
                    <i :class="lockAllDone ? 'fa-solid fa-lock' : 'fa-solid fa-lock-open'"></i>
                  </button>
                </li>
            <li v-if="isLiveCompetition" :class="reqOrderClass">
              {{ lblReqOrder }}
              <span class="badge" :class="reqOrderBadgeClass">{{ orderLabel }}</span>
            </li>
          </ul>
          <small class="text-muted d-block mt-2">{{ lblNotice }}</small>
        </div>
        <div class="order-trigger" v-if="isLiveCompetition && lockPrereqsMet">
          <button
              class="action-btn order"
              :disabled="!canOrder"
              @click="$emit('orderAthletes')"
          >
            <i class="fa-solid fa-recycle"></i> {{ $t('competitions.btn_order_athletes') }}
          </button>
        </div>
      </div>

      <!-- Botones de juez (grabaciones y evaluaciones) -->
      <div
          v-if="competition.started_at && !competition.finished_at"
          class="judge-actions-row"
      >
        <div class="judge-actions-left">
          <button
              v-if="isOwnerJudgeNormalized && isActiveTechOperator"
              class="action-btn record-btn"
              @click="handleOpenRecording"
          >
            <i class="fa-solid fa-circle-dot"></i> {{ $t('competitions.btn_start_recording') }}
          </button>

          <button
              v-if="isOwnerJudgeNormalized"
              class="action-btn open-scoring evaluate-btn"
              @click="$emit('openScoring', competition.id)"
          >
            <i class="fa-solid fa-gavel"></i> {{ $t('competitions.btn_start_scoring') }}
          </button>
        </div>

      </div>

      <CompetitionYoutubeControls v-if="showYoutubeControls" :competition="competition" />
    </template>

    <!-- Botón cancelar centrado al fondo -->
    <div v-if="showCancelButton" class="cancel-group">
      <button
          type="button"
          class="btn btn-secondary"
          @click="$emit('cancel')"
      >
        <i class="fa-solid fa-xmark"></i> {{ $t('competitions.btn_close') || $t('competitions.btn_cancel') || 'Cerrar' }}
      </button>
    </div>
    <ConfirmationModal
        ref="recordingWarningModal"
        :message="recordingWarningMessage"
        :showCancel="false"
        @confirmed="onRecordingWarningConfirmed"
    />
    <ConfirmationModal
      ref="lockAllModal"
      :message="lockAllModalMessage"
      :showCancel="true"
      @confirmed="confirmLockAllStages"
    />
  </div>
</template>

<script>
import {isOwnerJudgeFromCompetition, isOwnerOrganizer} from '@/helpers/roleUtils';
import { parseCompetitionDate, resolveCompetitionTimezone } from '@/helpers/timezoneDate';
import CompetitionYoutubeControls from "@/components/competitions/CompetitionYoutubeControls.vue";
import {mapState} from "vuex";
import ConfirmationModal from "@/components/competitions/ConfirmationModal.vue";

export default {
  name: "CompetitionActions",
  components: { ConfirmationModal, CompetitionYoutubeControls },
  props: {
    competition: {
      type: Object,
      required: true
    },
    countdownReached: {
      type: Boolean,
      default: false,
    },
    showYoutubeControls: {
      type: Boolean,
      default: true,
    },
    showCancelButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      recordingWarningMessage: '',
      recordingBlocked: false,
      lockAllBusy: false,
      roundSizesLocal: [],
      competitionUsersLocal: [],
      lockAllModalMessage: '',
    };
  },
  watch: {
    // Diversos alias posibles desde el backend
    'competition.round_sizes': {
      immediate: true,
      deep: true,
      handler(newVal) {
        const incoming = Array.isArray(newVal)
          ? [...newVal]
          : (newVal && typeof newVal === 'object') ? [newVal] : null;
        if (!incoming) return;
        this.roundSizesLocal = this.mergeRoundSizes(this.roundSizesLocal, incoming);
      },
    },
    'competition.roundSizes': {
      deep: true,
      handler(newVal) {
        const incoming = Array.isArray(newVal)
          ? [...newVal]
          : (newVal && typeof newVal === 'object') ? [newVal] : null;
        if (!incoming) return;
        this.roundSizesLocal = this.mergeRoundSizes(this.roundSizesLocal, incoming);
      }
    },
    'competition.round_size': {
      deep: true,
      handler(newVal) {
        const incoming = newVal && typeof newVal === 'object' ? [newVal] : null;
        if (!incoming) return;
        this.roundSizesLocal = this.mergeRoundSizes(this.roundSizesLocal, incoming);
      }
    },
    'competition.competition_users': {
      immediate: true,
      deep: true,
      handler(newVal) {
        const incoming = Array.isArray(newVal)
          ? [...newVal]
          : (newVal && typeof newVal === 'object') ? [newVal] : null;
        if (incoming) {
          this.competitionUsersLocal = this.mergeCompetitionUsers(this.competitionUsersLocal, incoming);
        }
      }
    },
    competitionUsersStore: {
      immediate: true,
      deep: true,
      handler(newVal) {
        const incoming = Array.isArray(newVal)
          ? [...newVal]
          : (newVal && typeof newVal === 'object') ? [newVal] : null;
        if (incoming) {
          this.competitionUsersLocal = this.mergeCompetitionUsers(this.competitionUsersLocal, incoming);
        }
      }
    },
    competitionUsers: {
      immediate: true,
      deep: true,
      handler() {
        // No-op: keep watcher for immediate sync
      }
    }
  },
  computed: {
    ...mapState('user', ['user', 'token']),
    ...mapState('competition_users', { competitionUsersStore: 'competitionUsers' }),
    isOwnerJudgeNormalized() {
      const uid = Number(this.user?.id || 0);
      if (!uid) return false;
      return this.competitionUsers.some(cu => {
        const roleId = Number(cu.role_id ?? cu.user?.role_id);
        const statusId = Number(cu.status_id ?? cu.user?.status_id);
        return Number(cu.user_id ?? cu.user?.id) === uid && roleId === 2 && statusId === 4;
      });
    },
    roundSizesSource() {
      // Prioriza la copia local fusionada; luego los distintos alias entrantes
      if (Array.isArray(this.roundSizesLocal) && this.roundSizesLocal.length) return this.roundSizesLocal;
      if (Array.isArray(this.competition?.round_sizes) && this.competition.round_sizes.length) return this.competition.round_sizes;
      if (Array.isArray(this.competition?.roundSizes) && this.competition.roundSizes.length) return this.competition.roundSizes;
      if (this.competition?.round_size) return [this.competition.round_size];
      return [];
    },
    competitionUsers() {
      const normalize = (list) => (Array.isArray(list) ? list : []).map(item => {
        const pivot = item?.pivot || {};
        // Prefiere campos en nivel raíz, luego pivot; asegura user_id consistente
        return {
          ...pivot,
          ...item,
          user_id: Number(item?.user_id ?? pivot.user_id ?? item?.user?.id ?? item?.id ?? 0),
          role_id: item?.role_id ?? pivot.role_id,
          status_id: item?.status_id ?? pivot.status_id,
          tech_operator_active: item?.tech_operator_active ?? pivot.tech_operator_active,
          tech_operator_qualified: item?.tech_operator_qualified ?? pivot.tech_operator_qualified,
        };
      });

      if (Array.isArray(this.competitionUsersLocal) && this.competitionUsersLocal.length) {
        return normalize(this.competitionUsersLocal);
      }
      if (Array.isArray(this.competition?.competition_users) && this.competition.competition_users.length) {
        return normalize(this.competition.competition_users);
      }
      return normalize(this.competitionUsersStore);
    },
    summary() {
      return this.competition?.athletes_summary || {};
    },
    activeStagesCount() {
      return this.roundSizesSource.filter(s => this.isTruthyFlag(s?.active)).length;
    },
    lockedStagesCount() {
      return this.roundSizesSource.filter(s => this.isTruthyFlag(s?.locked)).length;
    },
    totalStagesCount() {
      return this.roundSizesSource.length;
    },
    reqStagesLockMet() {
      // Si no hay etapas (modo solo calificación), se considera cumplido
      if (this.totalStagesCount === 0) return true;
      return this.lockedStagesCount === this.totalStagesCount;
    },
    competitionHasFee() {
      const raw = Number(this.competition?.competitions_info?.fees);
      return Number.isFinite(raw) && raw > 0;
    },
    isLiveCompetition() {
      return Number(this.competition?.type_id) === 2;
    },
    approvedAthletesList() {
      return this.competitionUsers.filter(u => this.isAthlete(u) && this.getStatus(u) === 4);
    },
    approvedApprovedSummary() {
      const fromSummary = Number(this.summary?.approved);
      return Number.isFinite(fromSummary) ? fromSummary : 0;
    },
    approvedJudgesList() {
      return this.competitionUsers.filter(u => this.isJudge(u) && this.getStatus(u) === 4);
    },
    minAthletes() {
      const byConfig = Number(this.competition?.athletes_min);
      return Number.isFinite(byConfig) && byConfig > 0 ? byConfig : 0;
    },
    lockPrereqsMet() {
      if (this.competitionHasFee) {
        return this.approvedAthletesCount >= this.minAthletes && this.paidAthletesCount === this.approvedAthletesCount;
      }
      return this.req1Met;
    },
    approvedAthletesCount() {
      const fromSummary = Number(this.summary.approved || 0);
      if (Number.isFinite(fromSummary) && fromSummary >= 0) return fromSummary;
      return this.approvedAthletesList.length;
    },
    orderedAt() {
      return this.competition?.athletes_ordered_at || null;
    },
    paidAthletesCount() {
      if (!this.competitionHasFee) return this.approvedAthletesCount;
      const fromSummary = Number(this.summary.paied || this.summary.paid || 0);
      if (fromSummary > 0) return fromSummary;
      return this.approvedAthletesList.filter(u => this.isFeePaid(u)).length;
    },
    approvedJudgesCount() {
      return this.approvedJudgesList.length;
    },
    techQualifiedActiveCount() {
      return this.approvedJudgesList.filter(u => this.isTruthyFlag(u?.tech_operator_qualified) && this.isTruthyFlag(u?.tech_operator_active)).length;
    },
    requiredJudges() {
      // Simple rule: only require a minimum of two approved judges
      return 2;
    },
    orderedAthletesCount() {
      return this.approvedAthletesList.filter(u => Number.isFinite(this.getOrder(u))).length;
    },
    competitionStarted() {
      return Boolean(this.competition?.started_at);
    },
    countdownMs() {
      const ts = this.parsePublishAt(this.competition?.publish_at);
      if (!ts || Number.isNaN(ts)) return Number.POSITIVE_INFINITY; // sin fecha no está listo
      return ts - Date.now();
    },
    countdownLabel() {
      if (this.req3Met) return 'OK';
      return '--';
    },
    req1Met() {
      return this.approvedApprovedSummary >= this.minAthletes;
    },
    reqJudgesMet() {
      if (this.requiredJudges <= 0) return false;
      return this.approvedJudgesCount >= this.requiredJudges;
    },
    req2Met() {
      if (!this.competitionHasFee) return true;
      return this.approvedAthletesCount > 0 && this.paidAthletesCount === this.approvedAthletesCount;
    },
    reqStagesLockClass() {
      return this.reqStagesLockMet ? 'text-success' : 'text-muted';
    },
    reqStagesLockBadgeClass() {
      if (this.reqStagesLockMet) return 'bg-success';
      return 'bg-secondary';
    },
    stagesLockLabel() {
      if (this.totalStagesCount === 0) return '--';
      return `${this.activeStagesCount}/${this.totalStagesCount}`;
    },
    reqOrderMet() {
      if (!this.isLiveCompetition) return true;
      const orderedAt = this.orderedAt;
      const orderedBy = this.competition?.athletes_ordered_by;
      const hasOrderFlags = this.competition && ('athletes_ordered_at' in this.competition || 'athletes_ordered_by' in this.competition);

      // Si el backend envía flags explícitos, solo consideramos OK cuando alguno es truthy
      if (hasOrderFlags) {
        return Boolean(orderedAt || orderedBy);
      }

      // Compatibilidad hacia atrás: considerar ordenado si todos tienen order_number
      const list = this.approvedAthletesList;
      if (!list.length) return false;
      return list.every(u => Number.isFinite(this.getOrder(u)));
    },
    orderLabel() {
      return this.reqOrderMet ? 'OK' : '--';
    },
    startTimestamp() {
      const raw = this.competition?.competitions_info?.date_start;
      const competitionTimeZone = resolveCompetitionTimezone(this.competition);
      const startDate = parseCompetitionDate(raw, competitionTimeZone);
      if (startDate) {
        return startDate.getTime();
      }
      const tsPublish = this.parsePublishAt(this.competition?.publish_at);
      return Number.isNaN(tsPublish) ? null : tsPublish;
    },
    req3Met() {
      if (this.countdownReached) return true;
      const ts = this.startTimestamp;
      if (!ts || Number.isNaN(ts)) return false;
      return Date.now() >= ts;
    },
    reqTechMet() {
      if (!this.isLiveCompetition) return true;
      return this.techQualifiedActiveCount >= 1;
    },
    allRequirementsMet() {
      return this.req1Met && this.reqJudgesMet && this.reqTechMet && this.req2Met && this.reqStagesLockMet && this.reqOrderMet && this.req3Met;
    },
    req1Class() {
      return this.req1Met ? 'text-success' : 'text-muted';
    },
    reqJudgesClass() {
      return this.reqJudgesMet ? 'text-success' : 'text-muted';
    },
    reqTechClass() {
      return this.reqTechMet ? 'text-success' : 'text-muted';
    },
    req2Class() {
      return this.req2Met ? 'text-success' : 'text-muted';
    },
    reqOrderClass() {
      return this.reqOrderMet ? 'text-success' : 'text-muted';
    },
    req3Class() {
      return this.req3Met ? 'text-success' : 'text-muted';
    },
    req1BadgeClass() {
      return this.req1Met ? 'bg-success' : 'bg-secondary';
    },
    reqJudgesBadgeClass() {
      return this.reqJudgesMet ? 'bg-success' : 'bg-secondary';
    },
    reqTechBadgeClass() {
      return this.reqTechMet ? 'bg-success' : 'bg-secondary';
    },
    req2BadgeClass() {
      return this.req2Met ? 'bg-success' : 'bg-secondary';
    },
    reqOrderBadgeClass() {
      return this.reqOrderMet ? 'bg-success' : 'bg-secondary';
    },
    req3BadgeClass() {
      return this.req3Met ? 'bg-success' : 'bg-secondary';
    },
    hasVisibleActions() {
      const hasPublish = Boolean(this.competition?.publish_at);
      const viewGroupVisible = hasPublish; // atletas / jueces siempre visibles si publicada
      const startButtonVisible = hasPublish && this.isOwnerOrganizer(this.user, this.competition) && !this.competitionStarted;
      const judgeActionsVisible = Boolean(this.competition?.started_at && !this.competition?.finished_at && this.isOwnerJudgeNormalized);
      const youtubeVisible = hasPublish && this.showYoutubeControls;
      const cancelVisible = this.showCancelButton;
      return viewGroupVisible || startButtonVisible || judgeActionsVisible || youtubeVisible || cancelVisible;
    },
    lblRequirementsTitle() {
      return this.$t('competitions.start_requirements.title');
    },
    lblReqMinAthletes() {
      return this.$t('competitions.start_requirements.min_approved');
    },
    lblReqJudges() {
      return this.$t('competitions.start_requirements.judges');
    },
    lblReqFeesPaid() {
      return this.$t('competitions.start_requirements.paid');
    },
    lblReqStagesLock() {
      return this.$t('competitions.start_requirements.stages_lock') || 'Confirma las etapas antes de empezar';
    },
    lblReqTech() {
      return this.$t('competitions.start_requirements.tech_operator') || 'Juez tecnico activo';
    },
    lblReqOrder() {
      return this.$t('competitions.start_requirements.order');
    },
    lblReqCountdown() {
      return this.$t('competitions.start_requirements.countdown');
    },
    lblNotice() {
      return this.$t('competitions.start_requirements.notice');
    },
    techStatusLabel() {
      if (!this.isLiveCompetition) return '--';
      return this.techQualifiedActiveCount >= 1 ? 'OK' : '-';
    },
    canOrder() {
      return this.lockPrereqsMet && !this.competitionStarted;
    },
    lockAllDisabled() {
      return this.lockAllBusy || this.reqStagesLockMet || this.totalStagesCount === 0 || this.activeStagesCount === 0 || !this.lockPrereqsMet;
    },
    lockAllDone() {
      return this.reqStagesLockMet;
    },
    authToken() {
      const direct = this.token;
      if (direct) return direct;
      const fromStorage = (localStorage.getItem('token') || '').replace(/"/g, '');
      return fromStorage || '';
    },
    currentCompetitionUser() {
      const uid = this.user?.id;
      if (!uid) return null;
      return this.competitionUsers.find(u => Number(u?.user?.id ?? u?.user_id) === Number(uid)) || null;
    },
    isActiveTechOperator() {
      const cu = this.currentCompetitionUser;
      if (!cu) return false;
      const isJudge = this.isJudge(cu);
      const qualified = this.isTruthyFlag(cu?.tech_operator_qualified);
      const active = this.isTruthyFlag(cu?.tech_operator_active);
      const approved = Number(this.getStatus(cu)) === 4;
      return isJudge && qualified && active && approved;
    }
  },
  methods:{
    isOwnerJudgeFromCompetition,
    isOwnerOrganizer,
    debugSummaryState() {
      console.log('CompetitionActions summary debug', {
        competitionId: this.competition?.id,
        athletes_min: this.competition?.athletes_min,
        summary: this.summary,
        approvedApprovedSummary: this.approvedApprovedSummary,
        approvedAthletesCount: this.approvedAthletesCount,
        paidAthletesCount: this.paidAthletesCount,
        req1Met: this.req1Met,
      });
    },
    isAthlete(user) {
      const roleStr = (user?.role || user?.pivot?.role || '').toString().toLowerCase();
      const roleId = Number(user?.role_id ?? user?.pivot?.role_id ?? 0);
      return roleStr === 'athlete' || roleStr === 'athletes' || roleId === 1;
    },
    isJudge(user) {
      const roleStr = (user?.role || user?.pivot?.role || '').toString().toLowerCase();
      const roleId = Number(user?.role_id ?? user?.pivot?.role_id ?? 0);
      return roleStr === 'judge' || roleStr === 'judges' || roleId === 2;
    },
    getStatus(user) {
      return Number(user?.status_id ?? user?.pivot?.status_id ?? 0);
    },
    getOrder(user) {
      const raw = user?.order_number ?? user?.pivot?.order_number;
      const num = Number(raw);
      return Number.isFinite(num) ? num : null;
    },
    isFeePaid(user) {
      return Number(user?.fees_paid ?? user?.pivot?.fees_paid ?? 0) === 1;
    },
    openLockAllConfirm() {
      if (this.lockAllBusy || this.lockAllDisabled) return;
      this.lockAllModalMessage = this.$t('competitions.rounds.lock_all_confirm')
        || 'Esta acción confirmará todas las etapas activas para ser usadas en la competencia. ¿Deseas continuar?';
      this.$refs.lockAllModal?.openModal();
    },
    async confirmLockAllStages() {
      if (this.lockAllBusy || this.lockAllDisabled) return;
      const competitionId = this.competition?.id;
      if (!competitionId) return;
      if (!this.authToken) {
        console.warn('Falta token para confirmar etapas');
        return;
      }
      this.lockAllBusy = true;
      try {
        const headers = { Authorization: `Bearer ${this.authToken}` };
        const { data } = await this.$axios.post(`/competitions/${competitionId}/round-sizes/lock-all`, {}, { headers });
        const updated = data?.round_sizes || data?.roundSizes || data;
        this.applyRoundSizesLockPatch(updated);
        this.$emit('roundSizesLockedAll', this.roundSizesLocal);
      } catch (err) {
        console.error('Error al confirmar todas las etapas', err?.response?.data || err);
      } finally {
        this.lockAllBusy = false;
        this.$refs.lockAllModal?.closeModal();
      }
    },
    applyRoundSizesLockPatch(list) {
      const incoming = Array.isArray(list)
        ? list
        : (list && typeof list === 'object') ? [list] : [];
      const base = Array.isArray(this.roundSizesLocal) ? this.roundSizesLocal : [];
      const merged = this.mergeRoundSizes(base, incoming);
      // Al confirmar lock-all, marcamos todas las etapas como bloqueadas en la copia local
      this.roundSizesLocal = merged.map(item => ({ ...item, locked: true }));
    },
    mergeCompetitionUsers(current, incoming) {
      const map = new Map();
      const score = (item) => {
        const roleScore = Number(item?.role_id === 2);
        const techScore = this.isTruthyFlag(item?.tech_operator_active);
        const statusScore = Number(item?.status_id === 4);
        const updated = Date.parse(item?.updated_at || item?.created_at || 0) || 0;
        return [roleScore, techScore, statusScore, updated];
      };
      const upsert = (item) => {
        if (!item) return;
        const pivot = item?.pivot || {};
        const uid = Number(item?.user_id ?? pivot.user_id ?? item?.user?.id ?? item?.id);
        if (!Number.isFinite(uid)) return;
        const prev = map.get(uid) || {};

        const incomingRole = Number(item?.role_id ?? pivot.role_id);
        const prevRole = Number(prev.role_id ?? prev?.pivot?.role_id);
        const role_id = incomingRole === 2 || prevRole === 2
          ? 2
          : (Number.isFinite(prevRole) ? prevRole : incomingRole);

        const incomingStatus = Number(item?.status_id ?? pivot.status_id);
        const prevStatus = Number(prev.status_id ?? prev?.pivot?.status_id);
        const status_id = incomingStatus === 4 || prevStatus === 4
          ? 4
          : (Number.isFinite(prevStatus) ? prevStatus : incomingStatus);

        const tech_operator_active_raw = item?.tech_operator_active ?? pivot.tech_operator_active;
        const tech_operator_qualified_raw = item?.tech_operator_qualified ?? pivot.tech_operator_qualified;

        const tech_operator_active = tech_operator_active_raw !== undefined && tech_operator_active_raw !== null
          ? this.isTruthyFlag(tech_operator_active_raw)
          : this.isTruthyFlag(prev.tech_operator_active);
        const tech_operator_qualified = tech_operator_qualified_raw !== undefined && tech_operator_qualified_raw !== null
          ? this.isTruthyFlag(tech_operator_qualified_raw)
          : this.isTruthyFlag(prev.tech_operator_qualified);

        const candidate = {
          ...prev,
          ...pivot,
          ...item,
          user_id: uid,
          role_id,
          status_id,
          tech_operator_active,
          tech_operator_qualified,
        };

        // Prefer the best-scoring candidate per user
        const prevScore = score(prev);
        const newScore = score(candidate);
        const better = newScore > prevScore;
        map.set(uid, better ? candidate : prev);
      };
      (Array.isArray(current) ? current : []).forEach(upsert);
      (Array.isArray(incoming) ? incoming : []).forEach(upsert);
      return Array.from(map.values());
    },
    mergeRoundSizes(current, incoming) {
      const map = new Map();
      (Array.isArray(current) ? current : []).forEach(item => {
        const key = item && item.id !== undefined ? Number(item.id) : null;
        if (key !== null && !Number.isNaN(key)) {
          map.set(key, { ...item, id: key });
        }
      });
      (Array.isArray(incoming) ? incoming : []).forEach(item => {
        const key = item && item.id !== undefined ? Number(item.id) : null;
        if (key !== null && !Number.isNaN(key)) {
          const prev = map.get(key) || {};
          map.set(key, { ...prev, ...item, id: key });
        }
      });
      return Array.from(map.values());
    },
    parsePublishAt(publishAt) {
      if (!publishAt) return null;
      if (typeof publishAt === 'string') {
        // Tratar la cadena como hora local si no trae zona para evitar adelantarla a UTC
        const normalized = publishAt.includes('T') ? publishAt : publishAt.replace(' ', 'T');
        const tsLocal = Date.parse(normalized);
        if (!Number.isNaN(tsLocal)) return tsLocal;
      }
      const ts = new Date(publishAt).getTime();
      return Number.isNaN(ts) ? null : ts;
    },
    isTruthyFlag(flag) {
      return flag === true || flag === 'true' || flag === 1 || flag === '1' || Number(flag) === 1;
    },
    isDesktopDevice() {
      if (typeof navigator === 'undefined') return true;
      const ua = navigator.userAgent || '';
      const mobileRe = /(Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone|Opera Mini|IEMobile|Mobile|Tablet)/i;
      return !mobileRe.test(ua);
    },
    handleOpenRecording() {
      if (!this.isDesktopDevice()) {
        this.recordingWarningMessage = this.$t('competitions.recording_desktop_only') || 'La grabación solo está disponible en PC.';
        this.recordingBlocked = true;
        this.$refs.recordingWarningModal?.openModal();
        return;
      }
      this.recordingBlocked = false;
      this.$emit('openRecording', this.competition.id);
    },
    onRecordingWarningConfirmed() {
      this.$refs.recordingWarningModal?.closeModal();
      if (this.recordingBlocked) {
        this.recordingBlocked = false;
        return;
      }
      this.$emit('openRecording', this.competition.id);
    },
  }

};
</script>

<style scoped>
.competition-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin-top: 15px;
}

.action-btn {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background-color 0.15s ease;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.16);
  filter: brightness(1.05);
}

.action-btn.start {
  background-color: #28a745;
  opacity: 1;
}
.action-btn.start:disabled {
  background-color: #94c9a2;
  cursor: not-allowed;
}
.action-btn.register {
  background-color: #28a745;
}

.action-btn.view-athletes {
  background-color: #17a2b8;
}

.action-btn.view-athletes {
  background-color: rgba(65, 101, 245, 0.4);
}

.action-btn.view-rounds {
  background-color: #ffc107;
}

.btn-secondary {
  background-color: #6c757d;
}
.view-group {
  background-color: #f1f1f1;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  justify-content: space-around;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
}

.view-group button {
  background-color: rgba(65, 101, 245, 0.4);
  color: #000;
  border: none;
  padding: 8px 12px;
  border-radius: 5px;
}

.evaluate-btn {
  background-color: #6f42c1;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 5px;
}
.judge-actions-row {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 8px;
}
.judge-actions-left {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.record-btn {
  background-color: #dc3545;
  color: white;
}

.cancel-group {
  width: 100%;
  display: flex;
  justify-content: center;
}

.start-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

.requirements-box {
  width: 100%;
  background: #f8f9fa;
  border: 1px solid #e2e6ea;
  border-radius: 8px;
  padding: 10px 12px;
}

.req-title {
  font-size: 13px;
  margin: 0 0 6px 0;
}

.req-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
}

.req-list .badge {
  margin-left: 6px;
}

.badge-action {
  border: none;
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  background-color: #6c757d;
  color: #fff;
  cursor: pointer;
}

.badge-action:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.badge-action-success {
  background-color: #28a745;
}
</style>
